// helpers/errorHelper.ts

import i18n from "i18next";
import {
  ERROR_TITLE_KEY_MAP,
  ERROR_DESCRIPTION_KEY_MAP,
} from "./errorCodeMap";

/**
 * ─────────────────────────────────────────────────────────────────────────
 * HOW THIS WORKS
 * ─────────────────────────────────────────────────────────────────────────
 * errorCodeMap.ts maps an API error code -> an i18n KEY (not the text
 * itself). The actual text lives in your locale JSON, e.g.
 * locales/en/errors.json:
 *
 *   "error_601_title": "User Not Found",
 *   "error_601_description": "We couldn't find an account with the..."
 *
 * This helper looks up the right key(s) for a code and asks i18next to
 * translate them, falling back to a generic message if a code, key, or
 * translation is missing.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * USAGE
 * ─────────────────────────────────────────────────────────────────────────
 *
 * 1) Places that only show a TITLE (e.g. a toast, a snackbar):
 *
 *      const title = gxGetErrorMessage(errorCode, "title");
 *      toast.error(title);
 *
 * 2) Places that only show a DESCRIPTION (e.g. inline form error text):
 *
 *      const message = gxGetErrorMessage(errorCode, "description");
 *      <Text>{message}</Text>
 *
 * 3) Places that show BOTH (e.g. an error dialog/modal with heading + body):
 *
 *      const { title, description } = gxGetErrorMessage(errorCode, "both");
 *      <Dialog title={title} body={description} />
 *
 * 4) With custom fallback keys (optional, defaults to "default_error_title"
 *    / "default_error_description"):
 *
 *      gxGetErrorMessage(errorCode, "title", { fallbackTitleKey: "network_error_title" });
 *
 * 5) With i18next interpolation values (optional):
 *
 *      gxGetErrorMessage(errorCode, "description", { options: { seconds: 30 } });
 *
 * If a code only has a title mapped (no description) and you ask for
 * "description" or "both", the missing half falls back to the generic
 * fallback text automatically - it will never throw or return undefined.
 * ─────────────────────────────────────────────────────────────────────────
 */

type ErrorMessageMode = "title" | "description" | "both";

interface GxGetErrorMessageOptions {
  /** i18n key to use when no title is found. Defaults to "default_error_title". */
  fallbackTitleKey?: string;
  /** i18n key to use when no description is found. Defaults to "default_error_description". */
  fallbackDescriptionKey?: string;
  /** Values passed through to i18next for interpolation (e.g. { seconds: 30 }). */
  options?: Record<string, unknown>;
}

export interface GxErrorMessage {
  title: string;
  description: string;
}

const DEFAULT_FALLBACK_TITLE_KEY = "default_error_title";
const DEFAULT_FALLBACK_DESCRIPTION_KEY = "default_error_description";

// ── DEBUG: set to false (or delete this block + the calls below) once the
// root cause is confirmed. Logs every piece of the resolution chain: the
// raw errorCode coming in, which key(s) errorCodeMap resolved it to, and
// what i18next actually returned for each key/namespace — including a
// direct i18n.exists() check per key so we can see definitively whether
// the key is missing vs. just in the wrong namespace.
const DEBUG_ERROR_HELPER = true;

function debugLogTranslate(
  label: string,
  primaryKey: string | undefined,
  fallbackKey: string,
  result: string,
) {
  if (!DEBUG_ERROR_HELPER) return;
  // eslint-disable-next-line no-console
  console.log(`[gxGetErrorMessage:${label}]`, {
    primaryKey,
    fallbackKey,
    resolvedText: result,
    primaryKeyExists: primaryKey ? i18n.exists(primaryKey) : undefined,
    fallbackKeyExists: i18n.exists(fallbackKey),
    // exists() checks with an explicit namespace guess — update "errors" to
    // whatever namespace your locale file is actually registered under, or
    // add more guesses here, to see if the key resolves once namespaced.
    primaryKeyExistsInErrorsNs: primaryKey
      ? i18n.exists(primaryKey, { ns: "errors" })
      : undefined,
    fallbackKeyExistsInErrorsNs: i18n.exists(fallbackKey, { ns: "errors" }),
    currentLanguage: i18n.language,
    loadedNamespaces: i18n.reportNamespaces?.getUsedNamespaces?.() ?? i18n.options?.ns,
    defaultNS: i18n.options?.defaultNS,
  });
}

function translate(
  primaryKey: string | undefined,
  fallbackKey: string,
  options?: Record<string, unknown>
): string {
  // Passing an array lets i18next try the primary key first and fall back
  // automatically (respecting its own language/namespace fallback chain),
  // instead of a separate i18n.exists() check that can miss valid
  // translations available in a fallback locale.
  const result = i18n.t(primaryKey ? [primaryKey, fallbackKey] : fallbackKey, options) as string;
  debugLogTranslate("translate", primaryKey, fallbackKey, result);
  return result;
}

function hasValidCode(errorCode?: string | number | null): errorCode is string | number {
  return (
    errorCode !== undefined &&
    errorCode !== null &&
    String(errorCode).trim() !== ""
  );
}

/**
 * Resolves a user-facing error TITLE only.
 */
export function gxGetErrorMessage(
  errorCode: string | number | null | undefined,
  mode: "title",
  opts?: GxGetErrorMessageOptions
): string;
/**
 * Resolves a user-facing error DESCRIPTION only.
 */
export function gxGetErrorMessage(
  errorCode: string | number | null | undefined,
  mode: "description",
  opts?: GxGetErrorMessageOptions
): string;
/**
 * Resolves BOTH title and description together.
 */
export function gxGetErrorMessage(
  errorCode: string | number | null | undefined,
  mode: "both",
  opts?: GxGetErrorMessageOptions
): GxErrorMessage;
export function gxGetErrorMessage(
  errorCode: string | number | null | undefined,
  mode: ErrorMessageMode = "title",
  opts: GxGetErrorMessageOptions = {}
): string | GxErrorMessage {
  const {
    fallbackTitleKey = DEFAULT_FALLBACK_TITLE_KEY,
    fallbackDescriptionKey = DEFAULT_FALLBACK_DESCRIPTION_KEY,
    options,
  } = opts;

  const code = hasValidCode(errorCode) ? String(errorCode).trim() : undefined;

  const titleKey = code ? ERROR_TITLE_KEY_MAP[code] : undefined;
  const descriptionKey = code ? ERROR_DESCRIPTION_KEY_MAP[code] : undefined;

  if (DEBUG_ERROR_HELPER) {
    // eslint-disable-next-line no-console
    console.log("[gxGetErrorMessage:input]", {
      errorCodeReceived: errorCode,
      errorCodeType: typeof errorCode,
      normalizedCode: code,
      mode,
      titleKeyFromMap: titleKey,
      descriptionKeyFromMap: descriptionKey,
    });
  }

  if (mode === "title") {
    return translate(titleKey, fallbackTitleKey, options);
  }

  if (mode === "description") {
    return translate(descriptionKey, fallbackDescriptionKey, options);
  }

  // mode === "both"
  return {
    title: translate(titleKey, fallbackTitleKey, options),
    description: translate(descriptionKey, fallbackDescriptionKey, options),
  };
}